"use client";

import { useState, useEffect, useRef } from 'react';
import { User, Upload, Save } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

export function UserAccountMenu() {
  const [name, setName] = useState('Guest');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tempName, setTempName] = useState('Guest');
  const [tempAvatarFile, setTempAvatarFile] = useState<File | null>(null);
  const [tempAvatarPreview, setTempAvatarPreview] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const storedName = localStorage.getItem('userName');
    const storedAvatar = localStorage.getItem('userAvatar');
    if (storedName) {
      setName(storedName);
      setTempName(storedName);
    }
    if (storedAvatar) {
      setAvatarUrl(storedAvatar);
      setTempAvatarPreview(storedAvatar);
    }
  }, []);

  const handleAvatarClick = () => {
    setTempName(name);
    setTempAvatarPreview(avatarUrl);
    setTempAvatarFile(null);
    setIsDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast({
          variant: 'destructive',
          title: 'File Too Large',
          description: 'Please select an image smaller than 2MB.',
        });
        return;
      }
      setTempAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSaveChanges = () => {
    try {
      localStorage.setItem('userName', tempName);
      setName(tempName);

      if (tempAvatarFile) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          localStorage.setItem('userAvatar', base64String);
          setAvatarUrl(base64String);
          toast({
            title: 'Success!',
            description: 'Your profile has been updated.',
          });
        };
        reader.readAsDataURL(tempAvatarFile);
      } else {
         toast({
            title: 'Success!',
            description: 'Your name has been updated.',
          });
      }

      setIsDialogOpen(false);
    } catch (error) {
       toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: 'Could not save profile. Browser storage might be full.',
      });
    }
  };


  return (
    <>
      <Button variant="ghost" size="icon" onClick={handleAvatarClick} aria-label="My Account">
        <Avatar className="h-8 w-8">
          <AvatarImage src={avatarUrl} alt={name} />
          <AvatarFallback>
            <User className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>My Account</DialogTitle>
            <DialogDescription>
              Personalize your experience. Your data is saved only in your browser.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="picture" className="text-right">
                Picture
              </Label>
              <div className="col-span-3 flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={tempAvatarPreview} />
                  <AvatarFallback>
                    <User className="h-8 w-8" />
                  </AvatarFallback>
                </Avatar>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" /> Upload
                </Button>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/png, image/jpeg, image/gif"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
                <Button type="button" variant="secondary">Cancel</Button>
            </DialogClose>
            <Button type="submit" onClick={handleSaveChanges}>
                <Save className="mr-2 h-4 w-4" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
